import React, { useState, useEffect, useRef } from "react";
import BackButton from "../BackButton/BackButton";
import { useFont } from "../../contexts/FontContext/FontContext";
import TextInput from "../TextInput/TextInput"; 
import styles from "./CreateLink.module.css";
import Dropdown from "../Dropdown/Dropdown";
import axios from "axios";

const CreateLink = ( { linkType, setExchangeOptionSelected, setReturnOptionSelected } ) => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const [businessName, setBusinessName] = useState("");
	const [customerData, setCustomerData] = useState({});
	const [generateLinkDisabled, setGenerateLinkDisabled] = useState(true);
	const [generateLinkButtonClass, setGenerateLinkButtonClass] = useState("");
	const [generatedLink, setGeneratedLink] = useState(null);
	const [noExchangeProductsSelectedWarning, setNoExchangeProductsSelectedWarning] = useState(false);
	const [validProductIds, setValidProductIds] = useState([]); //line items that have product ids that do not appear here should not be rendered in the dropdown. Products eligible for returns or exchanges need to be configured first
	const [integrationPartnerImage, setIntegrationPartnerImage] = useState("");
	const [lineItems, setLineItems] = useState([]);
	const [linkCopied, setLinkCopied] = useState(false);
	const [loadingOrderEditData, setLoadingOrderEditData] = useState(false);
	const [orderNumber, setOrderNumber] = useState("");
	const [uniqueOrderId, setUniqueOrderId] = useState("");
	const [selectedExchangeOrder, setSelectedExchangeOrder] = useState("");
	const [selectedItems, setSelectedItems] = useState([]);
	const [revealLink, setRevealLink] = useState(false);
	const [showLinkLoader, setShowLinkLoader] = useState(false);
	const token = localStorage.getItem("authToken");
	const fontSchemes = useFont();    
    const fontFamilyOne = fontSchemes.scheme_one; 
	const integrationPartnerImages = {"shopify" : "shopify.png", "woocommerce" : "woo_commerce.png", "magento" : "magento.png"};
	const previousLineItemsRef = useRef(lineItems);

	//See if there are any exchange products configured
	useEffect(() => {
		const checkExchangeProducts = async () => {
			try {
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/check_exchange_products/`, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"Authorization": `Bearer ${token}`,
					},
				});
				const data = await response.json();
				setValidProductIds(data.product_ids);
				if (!data.exchange_products_have_been_selected) { setNoExchangeProductsSelectedWarning(true); }
			} catch (error) { console.error("Failed to check for exchange products:", error); }
		};
		checkExchangeProducts();
	}, []);

    useEffect(() => {
		const fetchBusinessDetails = async () => {
			try {
				const response = await axios.get(`${apiUrl}/api/business_details/`, {
					headers: {
						"Content-Type": "application/json",
						"Authorization": `Bearer ${token}`,
					},
				});
				const { business_name, business_address, integration_partner } = response.data;
				setBusinessName(business_name);
				setIntegrationPartnerImage(integrationPartnerImages[integration_partner]);
			} catch (error) { 
				window.location.reload();
				console.error("Error fetching business details:", error); 
			}
		};
		fetchBusinessDetails();
	}, []);

	//copies link to clipboard
	const copyLink = () => {
	    navigator.clipboard.writeText(generatedLink)
	        .then(() => { setLinkCopied(true); })
	        .catch((error) => { console.error("Failed to copy link: ", error); });
	};

	const generateLink = async () => {
		setShowLinkLoader(true);
		try {
			// Send a request to the create_return_link endpoint
			const response = await axios.post(
				`${apiUrl}/api/create_return_link/`, {
					order_id: orderNumber,
					selected_items: selectedItems,
					business_name: businessName,
					customer_data: customerData,
					link_type: linkType,
					unique_id: uniqueOrderId
				},
				{
					headers: {
						"Content-Type": "application/json",
						"Authorization": `Bearer ${token}`,
					},
				}
			);
			if (response.data.link === "no items") { alert("no items available from this order!"); }
			else {
				setGeneratedLink(response.data.link); // Use the link from the response
				setLineItems([]);
				setSelectedItems([]);
			}
		} catch (error) { alert(`Error creating ${linkType} link:`, error); }
		setTimeout(() => { 
			setShowLinkLoader(false); 
			setRevealLink(true);
		}, 1000);	
	}

	const handleOrderNumberChange = (e) => {
	    let value = e.target.value;
	    if (value.startsWith('#')) { value = value.substring(1); } //omit # from input
	    setCustomerData({});
	    setUniqueOrderId("");
	    setOrderNumber(value); // Update order number
	    setSelectedExchangeOrder("");
	    setSelectedItems([]);  // Clear selected items
	    setLineItems([]);      // Clear line items
	    setRevealLink(false);  // Reset reveal link state
	    setLinkCopied(false);  // Reset link copied state
	};

	return (
		<div className={`${styles.create_link_container} ${fontFamilyOne}`}>
			{noExchangeProductsSelectedWarning && (
				<div className={styles.no_exchange_products_warning_box}>
					<img className={styles.exclamation_point} src={`${process.env.PUBLIC_URL}/images/exclamation_point.png`} />
					<p className={styles.no_exchange_products_warning}>Please select exchange products before starting an exchange.</p>
				</div>
			)}
			{lineItems.length === 0 && (<p className={`titles ${styles.create_link_instruction_text}`}>Provide order number to create {linkType} link.</p>)}
			{lineItems.length > 0 && (<p className={`titles ${styles.create_link_instruction_text}`}>Select items being returned.</p>)}
			<div className={styles.search_orders_container}>
	            <TextInput inputType={"create_link_search_orders"} value={orderNumber} onChange={handleOrderNumberChange} required />
	            {orderNumber && !revealLink && !showLinkLoader && (<Dropdown inputValue={orderNumber.trim()} integrationPartnerImage={integrationPartnerImage} selectedItems={selectedItems} setSelectedItems={setSelectedItems} lineItems={lineItems} setLineItems={setLineItems} orderNumber={orderNumber} setOrderNumber={setOrderNumber} customerData={customerData} setCustomerData={setCustomerData} linkType={linkType} selectedExchangeOrder={selectedExchangeOrder} setSelectedExchangeOrder={setSelectedExchangeOrder} apiUrl={apiUrl} token={token} setUniqueOrderId={setUniqueOrderId} loadingOrderEditData={loadingOrderEditData} setLoadingOrderEditData={setLoadingOrderEditData} validProductIds={validProductIds} />)}
	        </div>
			{!revealLink && (<button className={`login_buttons ${styles.generate_link_button} ${fontFamilyOne} ${orderNumber === "" || (selectedItems.length === 0 && linkType === "return") || (selectedExchangeOrder === "" && linkType === "exchange") ? "dimmed" : ""}`} onClick={() => generateLink()} disabled={(linkType === "return" && selectedItems.length === 0) || (linkType === "exchange" && selectedExchangeOrder === "") || loadingOrderEditData}>Generate link</button>)}
			{showLinkLoader && (<img className={styles.create_link_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
			{revealLink && (
				<div className={styles.generated_link_wrapper}>
					<p className={`${styles.send_link_text}`}>(Send this link to customer to begin {linkType})</p>
					<TextInput inputType="generated_link" value={generatedLink} required readOnly={true}/>  
					<div className={styles.link_buttons_container}>
						<button className={`login_buttons ${styles.copy_link} ${fontFamilyOne}`} onClick={() => copyLink()}>Copy link</button>
						<button className={`login_buttons ${styles.see_link} ${fontFamilyOne}`} onClick={() => window.open(generatedLink, "_blank")}>See link</button>
					</div>
					{linkCopied && (<p className={`${styles.copied_to_clipboard}`}>Link copied!</p>)}
				</div>
			)}
			<BackButton button_id={styles.create_link_back_button} image_id={styles.create_link_back_button_arrow} customBack={linkType === "exchange" ? () => setExchangeOptionSelected(null) : () => setReturnOptionSelected(null) } />
		</div>
	);
};

export default CreateLink;